import {
  AgeFilter,
  CallingStatusFilter,
  EngagementHistoryFilter,
  EventAttendanceFilter,
  isNonCompoundSearchFilter,
  isTemplatePlaceholder,
  MailingFilter,
  MembershipFilter,
  RosterFilter,
  SearchFilter,
  SearchTreeLeafNode,
  TaskFilter,
  TemplatePlaceholderWithFilterContext,
} from '@/types';

export function getPlaceholderFiltersInLeafNode(
  treeNode: SearchTreeLeafNode
): TemplatePlaceholderWithFilterContext[] {
  const filter = treeNode.filter as SearchFilter;
  if (!('value' in filter)) {
    // This means the filter is either an AllFilter, a NoneFilter or a
    // TestOnlyContactIdsRangeFilter, which lacks a value and doesn't make sense as a placeholder anyway
    return [];
  }

  if (filter.name === 'contactIds') return [];

  // TODO: Make this DRYer
  if (isTemplatePlaceholder(filter.value) && isNonCompoundSearchFilter(filter)) {
    return [{ ...filter.value, filterNameAndField: filter.name }];
  } else if (filter.name === 'age') {
    // TODO: Not sure if we need to consider age differently than the other simple ones?
    const ourAgeFilter = filter as AgeFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourAgeFilter.value)) {
      filtersToReturn.push({ ...ourAgeFilter.value, filterNameAndField: filter.name });
    }
    return filtersToReturn;
  } else if (filter.name === 'mailing') {
    const ourMembershipFilter = filter as MailingFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourMembershipFilter.value.mailingIds)) {
      filtersToReturn.push({
        ...ourMembershipFilter.value.mailingIds,
        filterNameAndField: `${filter.name}.mailingIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourMembershipFilter.value.mailingStatus)) {
      filtersToReturn.push({
        ...ourMembershipFilter.value.mailingStatus,
        filterNameAndField: `${filter.name}.mailingStatus` as const,
      });
    }
    return filtersToReturn;
  } else if (filter.name === 'membership') {
    const ourMembershipFilter = filter as MembershipFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourMembershipFilter.value.statusIds)) {
      filtersToReturn.push({
        ...ourMembershipFilter.value.statusIds,
        filterNameAndField: `${filter.name}.statusIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourMembershipFilter.value.branchIds)) {
      filtersToReturn.push({
        ...ourMembershipFilter.value.branchIds,
        filterNameAndField: `${filter.name}.branchIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourMembershipFilter.value.notRenewing)) {
      filtersToReturn.push({
        ...ourMembershipFilter.value.notRenewing,
        filterNameAndField: `${filter.name}.notRenewing` as const,
      });
    }

    return filtersToReturn;
  } else if (filter.name === 'task') {
    const ourTaskFilter = filter as TaskFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourTaskFilter.value.electionIds)) {
      filtersToReturn.push({
        ...ourTaskFilter.value.electionIds,
        filterNameAndField: `${filter.name}.electionIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourTaskFilter.value.kinds)) {
      filtersToReturn.push({
        ...ourTaskFilter.value.kinds,
        filterNameAndField: `${filter.name}.kinds` as const,
      });
    }
    if (isTemplatePlaceholder(ourTaskFilter.value.choices)) {
      filtersToReturn.push({
        ...ourTaskFilter.value.choices,
        filterNameAndField: `${filter.name}.choices` as const,
      });
    }
    if (isTemplatePlaceholder(ourTaskFilter.value.note)) {
      filtersToReturn.push({
        ...ourTaskFilter.value.note,
        filterNameAndField: `${filter.name}.note` as const,
      });
    }

    return filtersToReturn;
  } else if (filter.name === 'commsPreferences') {
    return isTemplatePlaceholder(filter.value.commsPreferencesLabels)
      ? [
          {
            ...filter.value.commsPreferencesLabels,
            filterNameAndField: `${filter.name}.commsPreferencesLabels`,
          },
        ]
      : [];
  } else if (filter.name === 'roster') {
    const ourRosterFilter = filter as RosterFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourRosterFilter.value.electionId)) {
      filtersToReturn.push({
        ...ourRosterFilter.value.electionId,
        filterNameAndField: `${filter.name}.electionId` as const,
      });
    }
    if (isTemplatePlaceholder(ourRosterFilter.value.electorateIds)) {
      filtersToReturn.push({
        ...ourRosterFilter.value.electorateIds,
        filterNameAndField: `${filter.name}.electorateIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourRosterFilter.value.rosterTypes)) {
      filtersToReturn.push({
        ...ourRosterFilter.value.rosterTypes,
        filterNameAndField: `${filter.name}.rosterTypes` as const,
      });
    }
    if (isTemplatePlaceholder(ourRosterFilter.value.votingPeriod)) {
      filtersToReturn.push({
        ...ourRosterFilter.value.votingPeriod,
        filterNameAndField: `${filter.name}.votingPeriod` as const,
      });
    }
    if (isTemplatePlaceholder(ourRosterFilter.value.shiftStatus)) {
      filtersToReturn.push({
        ...ourRosterFilter.value.shiftStatus,
        filterNameAndField: `${filter.name}.shiftStatus` as const,
      });
    }
    return filtersToReturn;
  } else if (filter.name === 'eventAttendance') {
    const ourEventAttendanceFilter = filter as EventAttendanceFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.statusIds)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.statusIds,
        filterNameAndField: `${filter.name}.statusIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.eventTypeIds)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.eventTypeIds,
        filterNameAndField: `${filter.name}.eventTypeIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.eventDateRange?.startDate)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.eventDateRange?.startDate,
        filterNameAndField: `${filter.name}.eventDateRange.startDate` as const,
      });
    }
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.eventDateRange?.endDate)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.eventDateRange?.endDate,
        filterNameAndField: `${filter.name}.eventDateRange.endDate` as const,
      });
    }
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.eventCampaignIds)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.eventCampaignIds,
        filterNameAndField: `${filter.name}.eventCampaignIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourEventAttendanceFilter.value.eventIds)) {
      filtersToReturn.push({
        ...ourEventAttendanceFilter.value.eventIds,
        filterNameAndField: `${filter.name}.eventIds` as const,
      });
    }
    return filtersToReturn;
  } else if (filter.name === 'engagementHistory') {
    const ourEngagementHistoryFilter = filter as EngagementHistoryFilter;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourEngagementHistoryFilter.value.activityIds)) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.activityIds,
        filterNameAndField: `${filter.name}.activityIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourEngagementHistoryFilter.value.activityStatusIds)) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.activityStatusIds,
        filterNameAndField: `${filter.name}.activityStatusIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourEngagementHistoryFilter.value.activityDateRange?.periodDetails)) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.activityDateRange?.periodDetails,
        filterNameAndField: `${filter.name}.activityDateRange.periodDetails` as const,
      });
    }
    if (
      isTemplatePlaceholder(ourEngagementHistoryFilter.value.activityDateRange?.displayedStartDate)
    ) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.activityDateRange?.displayedStartDate,
        filterNameAndField: `${filter.name}.activityDateRange.displayedStartDate` as const,
      });
    }
    if (
      isTemplatePlaceholder(ourEngagementHistoryFilter.value.activityDateRange?.displayedEndDate)
    ) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.activityDateRange?.displayedEndDate,
        filterNameAndField: `${filter.name}.activityDateRange.displayedEndDate` as const,
      });
    }
    if (isTemplatePlaceholder(ourEngagementHistoryFilter.value.subject)) {
      filtersToReturn.push({
        ...ourEngagementHistoryFilter.value.subject,
        filterNameAndField: `${filter.name}.subject` as const,
      });
    }
    return filtersToReturn;
  } else if (filter.name === 'callingStatus') {
    const ourCallingStatusFilter = filter as CallingStatusFilter;
    const dateRangeValue = ourCallingStatusFilter.value.callingStatusDateRange;
    const filtersToReturn = [];
    if (isTemplatePlaceholder(ourCallingStatusFilter.value.callingStatusLabels)) {
      filtersToReturn.push({
        ...ourCallingStatusFilter.value.callingStatusLabels,
        filterNameAndField: `${filter.name}.callingStatusLabels` as const,
      });
    }
    if (isTemplatePlaceholder(dateRangeValue?.callingPeriodQuantity)) {
      filtersToReturn.push({
        ...dateRangeValue.callingPeriodQuantity,
        filterNameAndField: `${filter.name}.callingStatusDateRange.callingPeriodQuantity` as const,
      });
    }
    if (isTemplatePlaceholder(dateRangeValue?.displayedStartDate)) {
      filtersToReturn.push({
        ...dateRangeValue.displayedStartDate,
        filterNameAndField: `${filter.name}.callingStatusDateRange.displayedStartDate` as const,
      });
    }
    if (isTemplatePlaceholder(dateRangeValue?.displayedEndDate)) {
      filtersToReturn.push({
        ...dateRangeValue.displayedEndDate,
        filterNameAndField: `${filter.name}.callingStatusDateRange.displayedEndDate` as const,
      });
    }

    if (isTemplatePlaceholder(ourCallingStatusFilter.value.callingStatusListIds)) {
      filtersToReturn.push({
        ...ourCallingStatusFilter.value.callingStatusListIds,
        filterNameAndField: `${filter.name}.callingStatusListIds` as const,
      });
    }
    if (isTemplatePlaceholder(ourCallingStatusFilter.value.callingStatusUserSubs)) {
      filtersToReturn.push({
        ...ourCallingStatusFilter.value.callingStatusUserSubs,
        filterNameAndField: `${filter.name}.callingStatusUserSubs` as const,
      });
    }
    if (isTemplatePlaceholder(ourCallingStatusFilter.value.callingStatusCampaignIds)) {
      filtersToReturn.push({
        ...ourCallingStatusFilter.value.callingStatusCampaignIds,
        filterNameAndField: `${filter.name}.callingStatusCampaignIds` as const,
      });
    }
    return filtersToReturn;
  } else {
    return [];
  }
}
